import { useCallback, useEffect, useState } from 'react'
import { api } from '@orisini/ui/lib/api'

/**
 * Data halaman depan, semuanya dari endpoint publik orisini.com sendiri.
 *
 * Tidak ada satu pun daftar produk, harga, atau versi agent yang ditulis ulang
 * di sini: katalognya milik tabel `products`/`plans`, dan versi agent milik
 * `latest.yml` di R2. Menambah produk keempat nanti tidak boleh mengharuskan
 * halaman ini disunting — kalau itu terjadi, sumber kebenarannya sudah bercabang.
 */

export interface Produk {
  code: string
  name: string
  description: string
  origin: string
}

export interface Paket {
  code: string
  name: string
  description: string
  priceMonthly: number
  priceYearly: number
  limits: {
    maxAccounts: number
    maxPostsPerMonth: number
    maxDevices: number
  }
  extraLimits?: Record<string, number>
  features: string[]
}

export interface Unduhan {
  version: string
  filename: string
  url: string
  sizeBytes: number | null
}

export interface Rilis {
  windows: Unduhan | null
  mac: Unduhan | null
  macAutoUpdate: boolean
}

/**
 * Produk yang benar-benar sudah bisa dipakai mengisi antrean.
 *
 * Katalog `products` tidak punya kolom "sudah jadi" — dan memang tidak
 * seharusnya, karena itu fakta rilis, bukan fakta penjualan. Sampai produknya
 * ada, halaman depan hanya menampilkannya sebagai "menyusul"; itu aturan yang
 * tertulis di PRODUCT.md, bukan tebakan.
 */
export const PRODUK_SIAP: ReadonlySet<string> = new Set(['pinboss'])

export function rupiah(nilai: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(nilai)
}

interface Muat<T> {
  data: T | null
  gagal: boolean
  memuat: boolean
  ulangi: () => void
}

/** GET sekali jalan, dengan keadaan memuat/gagal yang bisa dicoba ulang. */
function pakaiMuat<T>(path: string | null): Muat<T> {
  const [data, setData] = useState<T | null>(null)
  const [gagal, setGagal] = useState(false)
  const [memuat, setMemuat] = useState(path !== null)
  const [putaran, setPutaran] = useState(0)

  useEffect(() => {
    if (path === null) {
      setMemuat(false)
      return
    }

    let hidup = true
    setMemuat(true)
    setGagal(false)

    api
      .get<T>(path)
      .then((hasil) => {
        if (hidup) setData(hasil)
      })
      .catch(() => {
        if (hidup) setGagal(true)
      })
      .finally(() => {
        if (hidup) setMemuat(false)
      })

    return () => {
      hidup = false
    }
  }, [path, putaran])

  const ulangi = useCallback(() => setPutaran((n) => n + 1), [])

  return { data, gagal, memuat, ulangi }
}

/** Katalog produk publik — sumber yang sama dengan halaman unduh & langganan. */
export function useProduk() {
  const { data, ...sisa } = pakaiMuat<{ products: Produk[] }>('/products')
  return { produk: data?.products ?? [], ...sisa }
}

/** Versi agent terbaru. Rilis baru cukup diunggah; halaman ini ikut berubah. */
export function useRilis() {
  const { data, ...sisa } = pakaiMuat<Rilis>('/release')
  return { rilis: data, ...sisa }
}

/**
 * Paket Orisini — satu daftar untuk semua platform, sumber yang sama dengan
 * halaman langganan sehingga harga di sini tidak bisa berbeda dari yang
 * benar-benar ditagih.
 */
export function usePaket() {
  const { data, ...sisa } = pakaiMuat<{ plans: Paket[] }>('/billing/plans')
  return { paket: data?.plans ?? [], ...sisa }
}
